/** Shared helpers to match Microlab anatomy quiz questions to body structures. */

export function normalizeAnatomyLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\b(anatomy|quiz|system|region|left|right|of|the|a|an)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function legacyRightAnswerIndex(rightAnswer) {
  const raw = String(rightAnswer || "").trim().toLowerCase();
  if (raw === "1" || raw === "a") return 0;
  if (raw === "2" || raw === "b") return 1;
  if (raw === "3" || raw === "c") return 2;
  if (raw === "4" || raw === "d") return 3;
  const asNumber = Number(raw);
  if (asNumber >= 1 && asNumber <= 4) return asNumber - 1;
  return 0;
}

/** Normalize bank / legacy quiz rows into { answers:[{key,text,is_correct}], ... }. */
export function normalizeAnatomyQuizQuestion(row) {
  if (!row) return null;
  if (Array.isArray(row.answers) && row.answers.length) {
    const answers = row.answers.map((answer, i) => {
      if (typeof answer === "string") {
        return {
          answer_key: ["A", "B", "C", "D"][i] || String(i + 1),
          answer_text: answer,
          is_correct: false,
        };
      }
      return {
        answer_key: answer.answer_key || ["A", "B", "C", "D"][i] || String(i + 1),
        answer_text: answer.answer_text ?? "",
        is_correct: !!answer.is_correct,
      };
    });
    if (!answers.some((a) => a.is_correct) && row.right_answer != null) {
      const idx = legacyRightAnswerIndex(row.right_answer);
      answers.forEach((a, i) => {
        a.is_correct = i === idx;
      });
    }
    return {
      id: row.id,
      title: row.title || "",
      quiz_title: row.quiz_title || "",
      question_content: row.question_content || row.content || "",
      answers,
      anatomy_structure_id: row.anatomy_structure_id || null,
      anatomy_structure_name: row.anatomy_structure_name || null,
    };
  }

  const texts = [row.answer_a, row.answer_b, row.answer_c, row.answer_d].map((text) =>
    text == null ? "" : String(text)
  );
  const correctIndex = legacyRightAnswerIndex(row.right_answer);
  return {
    id: row.id,
    title: row.title || "",
    quiz_title: row.quiz_title || "",
    question_content: row.question_content || row.content || "",
    answers: texts.map((answer_text, i) => ({
      answer_key: ["A", "B", "C", "D"][i],
      answer_text,
      is_correct: i === correctIndex,
    })),
    anatomy_structure_id: row.anatomy_structure_id || null,
    anatomy_structure_name: row.anatomy_structure_name || null,
  };
}

export function resolveAnatomyStructureFromQuestion(question, structures) {
  if (!question || !Array.isArray(structures) || !structures.length) return null;
  if (question.anatomy_structure_id) {
    const byId = structures.find((item) => item.id === question.anatomy_structure_id);
    if (byId) return byId;
  }
  const named = String(question.anatomy_structure_name || "").trim();
  if (named) {
    const wanted = normalizeAnatomyLabel(named);
    const byName = structures.find((item) => normalizeAnatomyLabel(item.name) === wanted);
    if (byName) return byName;
  }

  const correctAnswer = Array.isArray(question.answers)
    ? question.answers.find((a) => a && a.is_correct)
    : null;
  const queries = [
    question.title,
    question.quiz_title,
    correctAnswer?.answer_text,
    ...(Array.isArray(question.answers) ? question.answers.map((a) => a?.answer_text) : []),
  ]
    .map((q) => normalizeAnatomyLabel(q))
    .filter((q) => q && q.length >= 4);

  let best = null;
  let bestScore = 0;
  structures.forEach((item) => {
    const name = normalizeAnatomyLabel(item.name || item.nameEn);
    if (!name || name.length < 3) return;
    queries.forEach((query, queryIdx) => {
      let score = 0;
      if (name === query) score = 100;
      else if (query.includes(name) || name.includes(query)) score = 70 + Math.min(name.length, 20);
      else {
        const parts = name.split(" ").filter((p) => p.length > 3);
        const hits = parts.filter((p) => query.includes(p)).length;
        if (hits && hits === parts.length) score = 55;
        else if (hits >= 2) score = 40;
      }
      if (queryIdx <= 1) score += 8;
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    });
  });
  return bestScore >= 55 ? best : null;
}
